// src/components/Sidebar.js
import React, { useMemo, useState, useEffect, useTransition, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOrgSelectorPresentation,
  resolveDashboardQueryOrgId,
  shouldShowOrganizationSelector,
  useAuth,
} from "../context/AuthContext";
import { fetchOrgSessions } from "../utils/orgData";
import { getOrganisationDisplayName } from "../utils/orgPresentation";
import { getOrgLogoAsset } from "../utils/brandAssets";
import { buildCoachThreadSessionList, COACH_THREAD_SYNC_EVENT } from "../utils/coachThread";
import {
  Home, MessageCircle, Users, Settings, Gamepad2, Sun, LogOut,
  UserPlus, CreditCard, BarChart2, ClipboardList, Building2, HelpCircle,
  ChevronDown, Smile
} from "lucide-react";

function Sidebar({ onNavigate, orgId, orgLogo, orgIcon, orgName, mode = "light", isMobile = false }) {
  const {
    user,
    logout,
    orgs,
    orgLoadStatus,
    orgLoadError,
    orgSelectionError,
    activeOrgId,
    orgAccessReady,
    setActiveOrgId,
    effectiveRole,
    viewRole,
    setViewRole,
  } = useAuth();
  const role = effectiveRole || user?.role || "member";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [, startTransition] = useTransition();

  const canCoach = role === "coach" || role === "superadmin";
  const canOpenChat = role === "member" || role === "coach" || role === "superadmin" || role === "investor";
  const canSeeUsers = role === "superadmin";             // Users page for superadmin only
  const canOrg = role === "admin" || role === "superadmin" || role === "investor";
  const canSupport = true;
  const supportPath = user?.role === "superadmin" ? "/support" : "/support/my";
  const canSwitchOrg = shouldShowOrganizationSelector({
    user,
    orgs,
    orgLoadStatus,
  });
  const [coachUnread, setCoachUnread] = useState(0);
  const selectorPresentation = getOrgSelectorPresentation(
    orgLoadStatus,
    orgs
  );

  const baseItems = useMemo(
    () => [
      { label: "Dashboard",   path: "/dashboard",   icon: <Home size={22} />,          show: true },
      { label: "Start Session", path: "/chat",      icon: <MessageCircle size={22} />, show: canOpenChat, badge: coachUnread },
      { label: "Daily Check-in", path: "/checkin",  icon: <Smile size={22} />,         show: true },
      { label: "Coach",       path: "/coach",       icon: <Settings size={22} />,      show: canCoach },
      { label: "Users",       path: "/users",       icon: <Users size={22} />,         show: canSeeUsers },
      { label: "Games",       path: "/games",       icon: <Gamepad2 size={22} />,      show: true },
      { label: "Preferences", path: "/preferences", icon: <Sun size={22} />,           show: true },
      { label: "Support",     path: supportPath,    icon: <HelpCircle size={22} />,    show: canSupport },
    ],
    [canCoach, canOpenChat, canSeeUsers, coachUnread, canSupport, supportPath]
  );

  const primaryMobilePaths = useMemo(
    () => new Set(["/dashboard", "/chat", "/games"]),
    []
  );

  const orgItems = useMemo(
    () => [
      { label: "Admin",      path: "/org/admin",   icon: <Settings size={22} /> },
      { label: "Invite",     path: "/org/invite",  icon: <UserPlus size={22} /> },
      { label: "Seat Usage", path: "/org/usage",   icon: <ClipboardList size={22} /> },
      { label: "Billing",    path: "/org/billing", icon: <CreditCard size={22} /> },
      { label: "Insights",   path: "/org/insights",icon: <BarChart2 size={22} /> },
    ],
    []
  );

  const isItemActive = (path) => pathname === path;
  const isOrgActive = pathname.startsWith("/org/");

  const resolvedOrgId = orgId || activeOrgId || user?.orgId || "org_default";
  const fallbackAvatarSrc = orgIcon || getOrgLogoAsset({ orgId: resolvedOrgId, logoUrl: orgLogo, mode, compact: true });
  const avatarSrc = user?.photoURL || fallbackAvatarSrc;
  const headerLogo = orgLogo || getOrgLogoAsset({ orgId: resolvedOrgId, mode, kind: "logo" });

  const handleLogout = async () => {
    try { await logout?.(); } finally { window.location.href = "/login"; }
  };

  const handleNav = useCallback((path) => {
    startTransition(() => {
      navigate(path);
    });
    onNavigate?.();
  }, [navigate, onNavigate, startTransition]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = resolveDashboardQueryOrgId(user, activeOrgId, orgAccessReady);
    if (!orgId) return undefined;
    const refresh = () => {
      fetchOrgSessions(user, orgId)
        .then((sessions) => {
          if (!mounted) return;
          const threadSessions = buildCoachThreadSessionList(sessions, user, user?.role || role || "member");
          setCoachUnread(threadSessions.filter((session) => session._unread).length);
        })
        .catch(() => {});
    };
    refresh();
    const onSync = () => refresh();
    window.addEventListener(COACH_THREAD_SYNC_EVENT, onSync);
    const interval = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      window.removeEventListener(COACH_THREAD_SYNC_EVENT, onSync);
      clearInterval(interval);
    };
  }, [user, activeOrgId, orgAccessReady, role]);

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img src={headerLogo} alt="Org logo" className="logo org-logo" />
        <h3 style={{ margin: "6px 0 4px" }}>{orgName || "Nia COACH Dashboard"}</h3>
        {user?.role && (
          <div className="role-chip">
            {user.role}
            {viewRole && viewRole !== user.role && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>(view: {viewRole})</span>
            )}
          </div>
        )}
        {(user?.role === "superadmin" || user?.role === "investor") && (
          <div style={{ marginTop: 10, width: "100%" }}>
            <label style={{ fontSize: 11, opacity: 0.7 }}>View as role</label>
            <select
              value={viewRole || ""}
              onChange={(e) => setViewRole(e.target.value)}
              style={{ marginTop: 6 }}
            >
              {user?.role === "superadmin" && <option value="">(Actual role)</option>}
              <option value="member">member</option>
              <option value="coach">coach</option>
              <option value="admin">admin</option>
              <option value="superadmin">superadmin</option>
              <option value="investor">investor</option>
            </select>
            {(viewRole === "investor" || user?.role === "investor") && (
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                View-only mode. Writes are still blocked for investors.
              </div>
            )}
          </div>
        )}
        {canSwitchOrg && (
          <div style={{ marginTop: 10, width: "100%" }}>
            <label style={{ fontSize: 11, opacity: 0.7 }}>Active org</label>
            <select
              aria-label="Active organisation"
              value={activeOrgId || ""}
              onChange={(e) => setActiveOrgId(e.target.value)}
              disabled={selectorPresentation.disabled}
              style={{ marginTop: 6 }}
            >
              {selectorPresentation.optionLabel && !(orgs || []).length && (
                <option value="">{selectorPresentation.optionLabel}</option>
              )}
              {(orgs || []).map((org) => (
                <option key={org.id} value={org.id}>
                  {getOrganisationDisplayName(org)}{org.archived ? " (archived)" : ""}
                </option>
              ))}
            </select>
            {(orgLoadError || orgSelectionError || (
              !(orgs || []).length ? selectorPresentation.statusMessage : ""
            )) && (
              <div role="status" style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
                {orgLoadError || orgSelectionError || selectorPresentation.statusMessage}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={!activeOrgId}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(activeOrgId || "");
                  } catch {}
                }}
              >
                Copy Org ID
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={!activeOrgId}
                onClick={() => handleNav("/org/admin")}
              >
                Open Org Admin
              </button>
            </div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {baseItems
          .filter((i) => i.show)
          .filter((i) => !isMobile || !primaryMobilePaths.has(i.path))
          .map((item) => {
          const active = isItemActive(item.path);
          return (
            <button
              key={item.path}
              type="button"
              className={`sidebar-btn${active ? " active" : ""}`}
              aria-current={active ? "page" : undefined}
              onClick={() => handleNav(item.path)}
            >
              <span className="sidebar-btn-icon">{item.icon}</span>
              <span className="sidebar-btn-label">{item.label}</span>
              {item.badge > 0 && (
                <span className="sidebar-badge" aria-label={`${item.badge} unread coach messages`}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}

        {canOrg && (
          <>
            <button
              type="button"
              className={`sidebar-btn sidebar-parent${isOrgActive ? " active" : ""}`}
              aria-expanded="true"
              aria-controls="org-submenu"
            >
              <span className="sidebar-btn-icon"><Building2 size={22} /></span>
              <span className="sidebar-btn-label">Manage Organisation</span>
              <span className="sidebar-caret" aria-hidden="true">
                <ChevronDown size={18} />
              </span>
            </button>

            <div
              id="org-submenu"
              className="sidebar-submenu open"
              role="region"
              aria-label="Manage Organisation"
            >
              {orgItems.map((item) => {
                const active = isItemActive(item.path);
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`sidebar-btn sidebar-sub${active ? " active" : ""}`}
                    aria-current={active ? "page" : undefined}
                    onClick={() => handleNav(item.path)}
                  >
                    <span className="sidebar-btn-icon">{item.icon}</span>
                    <span className="sidebar-btn-label">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-btn"
          onClick={() => handleNav("/profile")}
          aria-label="Open Profile"
          style={{ width: "100%" }}
        >
          <span className="sidebar-btn-icon">
            <img
              className="sidebar-avatar"
              src={avatarSrc}
              alt=""
              width={22}
              height={22}
              onError={(e) => { e.currentTarget.src = fallbackAvatarSrc; }}
            />
          </span>
          <span className="sidebar-btn-label">{user?.username || "Profile"}</span>
        </button>

        <button type="button" className="sidebar-btn logout-btn" onClick={handleLogout}>
          <span className="sidebar-btn-icon"><LogOut size={22} /></span>
          <span className="sidebar-btn-label">Logout</span>
        </button>
      </div>
    </aside>
  );
}

export default React.memo(Sidebar);
